import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowBigDown, BadgeCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const features = [
  {
    title: "Global Expertise",
    description:
      "Deep understanding of various markets and financial landscapes",
  },
  {
    title: "Tailored Solutions",
    description:
      "Customized advisory strategies to align with your unique business objectives",
  },
  {
    title: "Proven Track Record",
    description:
      "Decades of experience driving measurable results for businesses",
  },
  {
    title: "Collaborative Approach",
    description:
      "Working closely with clients to ensure seamless implementation and success",
  },
];

const BusinessAdvisory = () => {
  const navigate = useNavigate();
  return (
    <>
      <motion.div
        initial={{ opacity: 0.0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.3,
          duration: 0.8,
          ease: "easeInOut",
        }}
        className="relative py-10 sm:py-16 md:pt-24 lg:pt-36 lg:pb-24"
      >
        <div className="container">
          <TextParallaxContent
            imgUrl="/images/ourExpertise/4.jpg"
            subheading="Collaborate"
            heading="Strategic Finance & Business
Advisory for Sustainable Growth"
          >
            <ExampleContent />
          </TextParallaxContent>
          <TextParallaxContent
            imgUrl="/images/ourExpertise/5.jpg"
            subheading="Quality"
            heading="Never compromise."
          >
            <ExampleContentSecond />
          </TextParallaxContent>
          <TextParallaxContent
            imgUrl="/images/ourExpertise/6.jpg"
            subheading="Modern"
            heading="Dress for the best."
          >
            <ExampleContentThird />
          </TextParallaxContent>
        </div>
      </motion.div>
    </>
  );
};

export default BusinessAdvisory;

const IMG_PADDING = 50;

const TextParallaxContent = ({ imgUrl, subheading, heading, children }) => {
  return (
    <div
      style={
        {
          // paddingLeft: IMG_PADDING,
          // paddingRight: IMG_PADDING,
        }
      }
    >
      <div className="relative h-[70vh] lg:h-[80dvh]">
        <StickyImage imgUrl={imgUrl} />
        <OverlayCopy heading={heading} subheading={subheading} />
      </div>
      {children}
    </div>
  );
};

const StickyImage = ({ imgUrl }) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["end end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <motion.div
      style={{
        backgroundImage: `url(${imgUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: `calc(90vh - ${IMG_PADDING * 2}px)`,
        top: IMG_PADDING,
        scale,
      }}
      ref={targetRef}
      className="sticky z-0 overflow-hidden rounded-3xl"
    >
      <motion.div
        className="absolute inset-0 bg-neutral-950/70"
        style={{
          opacity,
        }}
      />
    </motion.div>
  );
};

const OverlayCopy = ({ subheading, heading }) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [250, -250]);
  const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75], [0, 1, 0]);

  return (
    <motion.div
      style={{
        opacity,
        y,
      }}
      ref={targetRef}
      className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center md:justify-center text-white gap-4 px-4 md:px-28 pt-20 md:pt-0"
    >
      <p className="mb-2 relative inline-block text-center text-2xl md:mb-4 md:text-3xl">
        {subheading}
        <span className="absolute left-1 bottom-[-0.5rem] w-full h-1 rounded-full bg-gradient-to-r from-secondary to-gold" />
      </p>
      <p className="text-center text-xl font-bold md:text-5xl text-white leading-tight">
        {heading}
      </p>
    </motion.div>
  );
};

const ExampleContent = () => {
  const navigate = useNavigate();

  return (
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-4 pb-10 md:pb-24 pt-12 md:grid-cols-12">
      <h2 className="col-span-1 text-2xl md:text-3xl font-bold md:col-span-4">
        Empowering Businesses Through Expertise
      </h2>
      <div className="col-span-1 md:col-span-8">
        <p className="mb-4 text-lg text-neutral-400 md:text-2xl">
          We specialize in providing comprehensive financial and business
          advisory services designed to unlock potential and drive success. By
          combining global insights, industry expertise, and innovative
          strategies, we help businesses navigate challenges, seize
          opportunities, and achieve their goals.
        </p>

        <button
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("why")
              .scrollIntoView({ behavior: "smooth" });
          }}
          className="w-full rounded-full mt-5 bg-gradient-to-tr from-gold/60 to-secondary/60 px-6 py-2.5 text-base font-semibold text-white transition-colors hover:bg-opacity-80 md:w-fit"
          // onClick={() => navigate("/contact")}
        >
          Learn more <ArrowBigDown className="inline" />
        </button>
      </div>
    </div>
  );
};

const ExampleContentSecond = () => {
  const navigate = useNavigate();

  return (
    <div
      className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-4 pb-24 pt-12 md:grid-cols-12"
      id="why"
    >
      <h2 className="col-span-1 text-2xl md:text-3xl font-bold md:col-span-4">
        Why Businesses Trust Scan?
      </h2>
      <div className="col-span-1 md:col-span-8">
        <p className="mb-4 text-lg text-neutral-400 md:text-2xl">
          Our finance and business advisory services are built on a foundation
          of trust, innovation, and results. Here’s what sets us apart
        </p>
        <div className="space-y-4">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <span className="text-gold mt-1">
                <BadgeCheck />
              </span>
              <p className="text-gray-400 text-base leading-6">
                <strong className="text-gold">{feature.title}:</strong>{" "}
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* <button
          className="w-full rounded-full mt-5 bg-gradient-to-tr from-gold/60 to-secondary/60 px-6 py-2.5 text-base font-semibold text-white transition-colors hover:bg-opacity-80 md:w-fit"
          onClick={() => navigate("/contact")}
        >
          Learn more <ArrowBigDown className="inline" />
        </button> */}
      </div>
    </div>
  );
};
const ExampleContentThird = () => {
  const navigate = useNavigate();

  return (
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-4 pb-0 md:pb-18 pt-12 md:grid-cols-12">
      <h2 className="col-span-1 text-2xl md:text-3xl font-bold md:col-span-4">
        Partnering with Key Influencers
      </h2>
      <div className="col-span-1 md:col-span-8">
        <p className="mb-4 text-lg text-neutral-400 md:text-2xl">
          Our finance and business advisory services are strengthened by our
          network of trusted Centres of Influence. These partnerships provide
          critical market insights, enhance decision-making, and enable us to
          deliver impactful results for our clients.
        </p>

        <button
          className="w-full rounded-full mt-5 bg-gradient-to-tr from-gold/60 to-secondary/60 px-6 py-2.5 text-base font-semibold text-white transition-colors hover:bg-opacity-80 md:w-fit"
          onClick={() => navigate("/contact")}
        >
          Learn more <ArrowBigDown className="inline" />
        </button>
      </div>
    </div>
  );
};
